import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import EqipRichTextEditor from "../../components/EqipRichTextEditor";
import { hasRichTextContent } from "./exhibitionUtils";

const ExhibitionManager = ({ inspectionUid, assignmentUid }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [exhibitions, setExhibitions] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [exhibitionToDelete, setExhibitionToDelete] = useState(null);
  const [editedRows, setEditedRows] = useState({});
  const [newRowId, setNewRowId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditingRow, setCurrentEditingRow] = useState(null);

  useEffect(() => {
    fetchExhibitions();
  // eslint-disable-next-line
  }, [inspectionUid]);

  useEffect(() => {
    if (newRowId) {
      setTimeout(() => {
        const element = document.querySelector(`[data-id="${newRowId}"] [data-field="code"]`);
        if (element) {
          element.click();
          element.focus();
        }
      }, 100);
    }
  }, [newRowId]);

  const fetchExhibitions = async () => {
    try {
      setIsLoading(true);
      const response = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspectionexhibitions`);
      setExhibitions(response.data || []);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching exhibitions:", error);
      setIsLoading(false);
    }
  };

  const handleCreate = async (newExhibition) => {
    try {
      setIsLoading(true);
      const payload = {
        code: newExhibition.code,
        description: newExhibition.description,
        inspection_uid: inspectionUid,
        assignment_uid: assignmentUid,
      };
      
      const response = await ieqipCoreServiceApi.post("/api/inspectionexhibitions", payload);
      
      if (response.data) {
        setExhibitions((prevExhibitions) =>
          prevExhibitions.map((e) =>
            e.inspection_exhibition_uid === newExhibition.inspection_exhibition_uid && e.isNew
              ? response.data
              : e
          )
        );
      } else {
        console.error('No data in create response');
        await fetchExhibitions();
      }
    } catch (error) {
      console.error("Error creating exhibition:", error);
      await fetchExhibitions();
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (updatedExhibition) => {
    try {
      setIsLoading(true);
      const payload = {
        code: updatedExhibition.code,
        description: updatedExhibition.description,
        inspection_uid: inspectionUid,
        assignment_uid: assignmentUid,
      };
      
      const response = await ieqipCoreServiceApi.put(
        `/api/inspectionexhibitions/${updatedExhibition.inspection_exhibition_uid}`, 
        payload
      );
      
      if (response.data) {
        // Update the exhibitions list with the updated exhibition from the response
        setExhibitions(prevExhibitions => 
          prevExhibitions.map(e => 
            e.inspection_exhibition_uid === updatedExhibition.inspection_exhibition_uid 
              ? response.data 
              : e
          )
        );
      } else {
        console.error('No data in update response');
        await fetchExhibitions();
      }
    } catch (error) {
      console.error("Error updating exhibition:", error);
      await fetchExhibitions();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (inspection_exhibition_uid) => {
    try {
      setIsLoading(true);
      await ieqipCoreServiceApi.delete(`/api/inspectionexhibitions/${inspection_exhibition_uid}`);
      setDeleteConfirmOpen(false);
      setExhibitionToDelete(null);
      fetchExhibitions();
    } catch (error) {
      console.error("Error deleting exhibition:", error);
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setExhibitionToDelete(row);
    setDeleteConfirmOpen(true);
  };

  /**
   * New editing API: keep controlled `rows` in sync and mark row dirty for Save (Title/code edits).
   */
  const processRowUpdate = (newRow) => {
    setExhibitions((prev) =>
      prev.map((r) =>
        r.inspection_exhibition_uid === newRow.inspection_exhibition_uid ? newRow : r
      )
    );
    setEditedRows((prev) => ({
      ...prev,
      [newRow.inspection_exhibition_uid]: {
        ...prev[newRow.inspection_exhibition_uid],
        code: newRow.code,
      },
    }));
    return newRow;
  };

  const handleSave = (row) => {
    const updatedRow = {
      ...row,
      ...editedRows[row.inspection_exhibition_uid]
    };

    // For new records, ensure we have both code and description
    if (row.isNew) {
      if (!updatedRow.code?.trim() || !hasRichTextContent(updatedRow.description)) {
        console.error('Both title and description are required for new records');
        return;
      }
      handleCreate(updatedRow);
    } else {
      handleUpdate(updatedRow);
    }
    // Clear the edited values for this row
    setEditedRows(prev => {
      const newEditedRows = { ...prev };
      delete newEditedRows[row.inspection_exhibition_uid];
      return newEditedRows;
    });
  };

  const handleDescriptionSave = () => {
    if (currentEditingRow) {
      // Update editedRows
      setEditedRows(prev => ({
        ...prev,
        [currentEditingRow.id]: {
          ...prev[currentEditingRow.id],
          description: currentEditingRow.value
        }
      }));

      // Update exhibitions state
      setExhibitions(prev => prev.map(exhibition => 
        exhibition.inspection_exhibition_uid === currentEditingRow.id
          ? { ...exhibition, description: currentEditingRow.value }
          : exhibition
      ));
    }
    setEditDialogOpen(false);
    setCurrentEditingRow(null);
  };

  const handleMoveRow = async (row, direction) => {
    const currentIndex = exhibitions.findIndex(e => e.inspection_exhibition_uid === row.inspection_exhibition_uid);
    if (
      (direction === 'up' && currentIndex === 0) || 
      (direction === 'down' && currentIndex === exhibitions.length - 1)
    ) {
      return;
    }

    try {
      setIsLoading(true);
      const endpoint = direction === 'up' 
        ? `/api/inspectionexhibitions/${row.inspection_exhibition_uid}/move-up`
        : `/api/inspectionexhibitions/${row.inspection_exhibition_uid}/move-down`;
      
      await ieqipCoreServiceApi.post(endpoint);
      
      // Refresh the exhibitions list to get the updated order
      await fetchExhibitions();

      // If the row has unsaved changes, update the editedRows state
      if (editedRows[row.inspection_exhibition_uid]) {
        const updatedEditedRows = { ...editedRows };
        delete updatedEditedRows[row.inspection_exhibition_uid];
        setEditedRows(updatedEditedRows);
      }
    } catch (error) {
      console.error(`Error moving exhibition ${direction}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: "sequence",
      headerName: "#",
      width: 50,
      flex: 0,
      valueGetter: (params) => {
        const rowIndex = exhibitions.findIndex(row => row.inspection_exhibition_uid === params.row.inspection_exhibition_uid);
        return rowIndex + 1;
      },
    },
    {
      field: "code",
      headerName: "Title",
      flex: 2,
      minWidth: 200,
      editable: true,
      required: true,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 4,
      minWidth: 300,
      editable: false,
      required: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            setCurrentEditingRow({ id: params.row.inspection_exhibition_uid, value: params.value });
            setEditDialogOpen(true);
          }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              '& p': {
                margin: 0,
              },
              '& *': {
                maxWidth: '100%',
              },
            }}
            dangerouslySetInnerHTML={{ __html: params.value || '' }}
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      flex: 0,
      renderCell: ({ row }) => (
        <Box>
          {row.isNew ? (
            <>
              <IconButton
                color="primary"
                onClick={() => handleSave(row)}
                disabled={
                  !(
                    { ...row, ...editedRows[row.inspection_exhibition_uid] }.code?.trim() &&
                    hasRichTextContent(
                      { ...row, ...editedRows[row.inspection_exhibition_uid] }.description
                    )
                  )
                }
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => {
                  setExhibitions(prev => prev.filter(e => e.inspection_exhibition_uid !== row.inspection_exhibition_uid));
                  setEditedRows(prev => {
                    const newEditedRows = { ...prev };
                    delete newEditedRows[row.inspection_exhibition_uid];
                    return newEditedRows;
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                color="primary"
                onClick={() => handleSave(row)}
                disabled={!editedRows[row.inspection_exhibition_uid]}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleDeleteClick(row)}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={() => handleMoveRow(row, 'up')}
            disabled={
              row.isNew ||
              exhibitions.findIndex(e => e.inspection_exhibition_uid === row.inspection_exhibition_uid) === 0
            }
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleMoveRow(row, 'down')}
            disabled={
              row.isNew ||
              exhibitions.findIndex(e => e.inspection_exhibition_uid === row.inspection_exhibition_uid) === exhibitions.length - 1
            }
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleAddNew = () => {
    const newId = `new-${Date.now()}`;
    setExhibitions([
      {
        inspection_exhibition_uid: newId,
        code: '',
        description: '',
        isNew: true
      },
      ...exhibitions
    ]);
    // Initialize edited rows for the new record
    setEditedRows(prev => ({
      ...prev,
      [newId]: {
        code: '',
        description: ''
      }
    }));
    setNewRowId(newId);
  };

  return (
    <Box m="10px">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 100px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Exhibitions List
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
              >
                Add Exhibition
              </Button>
            </Box>
            <DataGrid
              rows={exhibitions}
              columns={columns}
              loading={isLoading}
              sx={{ height: 'calc(100% - 60px)' }}
              disableRowSelectionOnClick
              editMode="cell"
              getRowId={(row) => row.inspection_exhibition_uid}
              experimentalFeatures={{ newEditingApi: true }}
              processRowUpdate={processRowUpdate}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this exhibition?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleDelete(exhibitionToDelete?.inspection_exhibition_uid)} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Description Editor Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Description</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minHeight: '300px' }}>
            <EqipRichTextEditor
              value={currentEditingRow?.value || ''}
              onChange={(newValue) => {
                setCurrentEditingRow(prev => ({
                  ...prev,
                  value: newValue
                }));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDescriptionSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExhibitionManager; 